import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { LayoutService } from '../../core/service/layout.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ReviewService } from '../service/review.service';
import { CohortStatus } from '../model/CohortStatus';
import { ReviewStage } from '../model/ReviewStage';
import { PerformanceIndicator } from '../model/PerformanceIndicator';
import { CaseSplitPipe } from '../../shared/pipe/CaseSplit.pipe';
import { PagerComponent } from '../../shared/pager.component';
import { FriendlyBooleanPipe } from '../../shared/pipe/FriendlyBoolean.pipe';
import { ReviewView } from '../model/ReviewView';

@Component({
  templateUrl: './qaCohort.component.html',
  styleUrls: ['../list.scss', './review.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QaCohortComponent implements OnInit, AfterViewInit {

  loading = false;
  data = [];
  CohortStatus = CohortStatus;
  cohortId;
  ReviewStage = ReviewStage;
  cohort: any = {};
  PerformanceIndicator = PerformanceIndicator;
  filterParams;
  activeFilterParams;
  activeFilterParamsInfo: { key: string; value: any }[] = [];
  panel: any;
  heading: string | undefined;
  reviewers: any[] = [];
  assigningItem: any;
  @ViewChild(PagerComponent,) pager!: PagerComponent;

  reviewView;
  ReviewView = ReviewView;

  editing: {
    id?: any;
    lead?: any;
    secondary?: any;
    row?: any;
    isValid?: boolean;
    validationMessage?: string;
  } = {};

  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    public layout: LayoutService, public service: ReviewService, private caseSplitPipe: CaseSplitPipe, private friendlyBooleanPipe: FriendlyBooleanPipe) {
    this.cohortId = this.activatedRoute.snapshot.params['cohortId'];
    this.filterParams = this.emptyFilterParams();
    this.activeFilterParams = this.emptyFilterParams();

    const params = Object.assign({}, activatedRoute.snapshot.queryParams);

    this.reviewView = params.reviewView;
    delete params.reviewView;

    for (const key in params) {

      if (params.hasOwnProperty(key)) {
        if (params[key] === 'true') {
          params[key] = true;
        }
        if (params[key] === 'false') {
          params[key] = false;
        }
      }
    }
    Object.assign(this.filterParams, params);
  }

  ngOnInit() {
    this.service.getReviewers().subscribe(data => {
      this.reviewers = data.map((reviewer: any) => {

        reviewer.name = reviewer.name.slice(reviewer.name.indexOf(' ') + 1);  // remove salutation

        return reviewer;
      });
    });
  }

  ngAfterViewInit() {
    this.load();
  }

  load() {
    this.pager.load(Object.assign({ id: this.cohortId, reviewView: this.reviewView }, this.filterParams));
  }



  emptyFilterParams(): { [key: string]: any } {
    return {
      reviewStage: undefined,
      reviewer: undefined,
      finalAssessment: undefined,
      excludedFromPublish: undefined,
      qaManagerApproved: undefined,
      qaApproved: undefined
    };
  }

  returnToSummary() {
    this.router.navigate(['./revalidation/review']);
  }

  onLoad(data: any) {
    this.data = data.reviews;
    this.cohort = data.cohortStatistics;
    this.setActiveFilterParams();
    this.loading = false;
  }

  toggleEdit(row: any) {
    if (row.id === this.editing.id) {
      this.editing = { id: undefined, lead: undefined, secondary: undefined, row: undefined };
    } else {
      this.editing = {
        row,
        id: row.id,
        lead: row.leadReviewerUserId,
        secondary: row.secondaryReviewerUserId,
        isValid: true
      };
    }
  }

  cancelEdit() {
    this.editing = { id: undefined, lead: undefined, secondary: undefined, row: undefined };
  }


  setActiveFilterParams() {
    this.activeFilterParams = Object.assign({}, this.filterParams);

    const formatParamValue = (key: string, value: any) => {
      switch (key) {
        case 'excludedFromPublish':
        case 'qaManagerApproved':
        case 'qaApproved':
          return this.caseSplitPipe.transform(key) + ': ' + this.friendlyBooleanPipe.transform(value);
        case 'reviewStage':
          return this.caseSplitPipe.transform(ReviewStage[value] ?? value);
        case 'finalAssessment':
          return this.caseSplitPipe.transform(PerformanceIndicator[value] ?? value);
        case 'reviewer':
          return this.reviewers.find(reviewer => reviewer.userId === value)?.name;
        default:
          return this.caseSplitPipe.transform(key) + ': ' + value;
      }
    };

    if (this.data.length) {
      this.panel = undefined;
    }

    this.activeFilterParamsInfo = Object.keys(this.activeFilterParams).map(key => {
      if (this.activeFilterParams[key] !== undefined) {
        return { key, value: formatParamValue(key, this.activeFilterParams[key]) };
      }
      return false;
    }).filter(v => !!v);
  }

  openReview(row: any) {
    this.router.navigate(['./revalidation/review', row.id], {
      queryParams: { stage: 'qa', cohortId: this.cohortId }
    });
  }

  togglePanel(panel: any) {
    if (this.panel === panel) {
      this.panel = undefined;
    } else {
      this.panel = panel;
    }
  }


  clearAllFilters() {
    this.filterParams = this.emptyFilterParams();
    if (this.hasActiveFilterParams) {
      this.load();
    }

  }

  clearFilter(filter: string) {
    this.filterParams[filter] = undefined;
    if (this.hasActiveFilterParams) {
      this.load();
    }
  }



  get filterParamsAreActive() {
    if (!this.hasActiveFilterParams) {
      return false;
    }
    for (const key in this.filterParams) {
      if (this.filterParams.hasOwnProperty(key)) {
        if (this.filterParams[key] !== undefined && this.activeFilterParams[key] === undefined) {
          return false;
        }
        if (this.activeFilterParams[key] !== undefined && this.filterParams[key] === undefined) {
          return false;
        }
        if (this.activeFilterParams[key] !== this.filterParams[key]) {
          return false;
        }
      }
    }
    return true;
  }

  get hasActiveFilterParams() {
    for (const key in this.activeFilterParams) {
      if (this.activeFilterParams[key] !== undefined && this.activeFilterParams[key] !== '') {
        return true;
      }
    }
    return false;
  }

  get hasFilterParams() {
    for (const key in this.filterParams) {
      if (this.filterParams[key] !== undefined && this.filterParams[key] !== '') {
        return true;
      }
    }
    return false;
  }

}

